import { ArrowRightOutlined, LockOutlined } from '@ant-design/icons'
import UserIcon from '@assets/CustomIcons/user_edited.svg'
import { OrderProducts } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { GraviButton, Texto, Vertical } from '@gravitate-js/excalibrr'
import { convertTimeSpanToReadable, ReadableDifference, ShowRunOutWarningClass } from '@utils/general'
import { Tooltip } from 'antd'
import {
  CustomerColumn,
  DeliveryWindowColumn,
  LoadingWindowColumn,
} from '@pages/OrderManagement/components/Grid/Columns/DefaultColumnDefs'
import type { ColDef } from 'ag-grid-community'
import type { RunStatus } from '@/hooks/useRouteOptimization'
import useRunoutFilter from '@/pages/OrderManagement/components/Grid/Filters/Runout/deprecated_useRunoutFilter'

export const orderColumnDefs = (checkbox?: boolean, lockedOrders?: RunStatus[]): ColDef[] => {
  if (checkbox)
    return [
      {
        field: 'id',
        headerName: '',
        minWidth: 90,
        maxWidth: 90,
        hide: !checkbox,
        checkboxSelection: checkbox,
        headerCheckboxSelection: checkbox,
        headerCheckboxSelectionFilteredOnly: checkbox,
        cellRenderer: (params) => {
          if (!params?.node?.selectable) {
            const user = lockedOrders?.find((o) => o.order_id === params?.data?.id)?.created_by
            const title = (
              <Vertical>
                <Texto appearance='white'>Locked by Optimizer</Texto>
                <Texto appearance='white'>
                  <img
                    src={UserIcon}
                    style={{
                      width: 24,
                      height: 24,
                    }}
                    className='mr-2'
                  />
                  {user}
                </Texto>
              </Vertical>
            )
            return (
              <Tooltip title={title}>
                <GraviButton icon={<LockOutlined />} className='ghost-gravi-button' />
              </Tooltip>
            )
          }
        },
      },
      {
        field: 'number',
        headerName: 'Number',
      },
      {
        field: 'po',
        headerName: 'PO #',
      },
      {
        field: 'drops',
        headerName: 'Destination',
        autoHeight: true,
        minWidth: 200,
        valueGetter: (params) => params?.data?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`),
        cellRenderer: (params) => (
          <>
            {params?.data?.drops?.map((d, i) => {
              return (
                <div key={i}>
                  {d.length > 1 ? <b>{d.length} More</b> : <b>{d?.location_name}</b>},
                  <span className='ml-1'>{d.city}</span>
                </div>
              )
            })}
          </>
        ),
      },
      {
        field: 'loads',
        headerName: 'Terminal',
        autoHeight: true,
        minWidth: 150,
        valueGetter: (params) => params?.data?.loads?.map((load) => load.location_name),
        cellRenderer: (params) => (
          <>
            {params?.data?.loads.map((d, i) => {
              const locationName = d.location_short_name || d.location_name
              const showArrow = i !== params?.data?.loads.length - 1
              return (
                <div key={i}>
                  {locationName}
                  {showArrow && <ArrowRightOutlined style={{ color: 'var(--theme-color-2)' }} className='pl-1 pr-2' />}
                </div>
              )
            })}
          </>
        ),
      },
      {
        field: 'products',
        headerName: 'Products',
        valueGetter: (params) => params?.data?.product_ratios?.map((product) => product.name),
        cellRenderer: (params) => (
          <div style={{ height: 'fit-content' }}>
            <OrderProducts order={params.data} />
          </div>
        ),
        minWidth: 150,
        autoHeight: true,
      },
      DeliveryWindowColumn(),
      LoadingWindowColumn(),
      {
        field: 'runout',
        headerName: 'Runout',
        filter: useRunoutFilter,
        valueGetter: (params) => params?.data?.dispatch_window_end,
        cellRenderer: (params) => (
          <span className={ShowRunOutWarningClass(params?.data?.dispatch_window_end)}>
            {ReadableDifference(
              params?.data?.dispatch_window_end,
              undefined,
              true,
              params?.data?.type?.toLowerCase() === 'backhaul'
            )}
          </span>
        ),
        sort: 'asc',
      },
      {
        headerName: 'Turn Time',
        field: 'turn_time',
        valueGetter: (params) => params?.data?.turn_time && convertTimeSpanToReadable(params?.data?.turn_time),
      },

      {
        field: 'market',
        headerName: 'Market',
      },
      {
        field: 'sector',
        headerName: 'Sector',
        cellRenderer: (params) =>
          params?.data?.sector && <Texto textTransform='capitalize'>{params?.data?.sector}</Texto>,
      },
      CustomerColumn(),
    ]

  return [
    {
      field: 'id',
      headerName: '',
      maxWidth: 50,

      cellRenderer: (params) => {
        if (!checkbox) return <LockOutlined />
      },
    },
    {
      field: 'number',
      headerName: 'Number',
    },
    {
      field: 'po',
      headerName: 'PO #',
    },
    {
      field: 'drops',
      headerName: 'Destination',
      valueGetter: (params) => params?.data?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`),
      cellRenderer: (params) => (
        <>
          {params?.data?.drops?.map((d, i) => {
            return (
              <div key={i}>
                {d.length > 1 ? <b>{d.length} More</b> : <b>{d?.location_name}</b>},
                <span className='ml-1'>{d.city}</span>
              </div>
            )
          })}
        </>
      ),
    },
    {
      field: 'loads',
      headerName: 'Terminal',
      valueGetter: (params) => params?.data?.loads?.map((load) => load.location_name),
      cellRenderer: (params) => (
        <>
          {params?.data?.loads.map((d, i) => {
            const locationName = d.location_short_name || d.location_name
            const showArrow = i !== params?.data?.loads.length - 1
            return (
              <div key={i}>
                {locationName}
                {showArrow && <ArrowRightOutlined style={{ color: 'var(--theme-color-2)' }} className='pl-1 pr-2' />}
              </div>
            )
          })}
        </>
      ),
    },
    {
      field: 'products',
      headerName: 'Products',
      valueGetter: (params) => params?.data?.product_ratios?.map((product) => product.name),
      cellRenderer: (params) => <OrderProducts order={params.data} />,
      minWidth: 200,
    },
    DeliveryWindowColumn(),
    LoadingWindowColumn(),
    {
      field: 'runout',
      headerName: 'Runout',
      filter: useRunoutFilter,
      valueGetter: (params) => params?.data?.dispatch_window_end,
      cellRenderer: (params) => (
        <span className={ShowRunOutWarningClass(params?.data?.dispatch_window_end)}>
          {ReadableDifference(
            params?.data?.dispatch_window_end,
            undefined,
            true,
            params?.data?.type?.toLowerCase() === 'backhaul'
          )}
        </span>
      ),
      sort: 'asc',
    },
    {
      headerName: 'Turn Time',
      field: 'turn_time',
      valueGetter: (params) => params?.data?.turn_time && convertTimeSpanToReadable(params?.data?.turn_time),
    },

    {
      field: 'market',
      headerName: 'Market',
    },
    {
      field: 'sector',
      headerName: 'Sector',
      cellRenderer: (params) =>
        params?.data?.sector && <Texto textTransform='capitalize'>{params?.data?.sector}</Texto>,
    },
    {
      field: 'reason',
      headerName: 'Reason',
    },
  ]
}
